/**
 * Super Admin Authentication Service
 * Handles authentication for Tiya Technology super administrators
 */
import API_CONFIG from '../../config';
import axios from 'axios';

const API_URL = API_CONFIG.BASE_URL;

/**
 * Login super admin with username and password
 * @param {string} username Super admin username
 * @param {string} password Super admin password
 * @returns {Promise} Promise with super admin data and token
 */
export const superAdminLogin = async (username, password) => {
  try {
    const response = await axios.post(
      `${API_URL}/superadmin/auth/login`,
      { username, password },
      {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      }
    );

    const data = response.data;

    // Store super admin auth data in localStorage (separate from regular users)
    if (data.accessToken) {
      storeSuperAdminData(data);
    }

    return data;
  } catch (error) {
    console.error('Super Admin Login error details:', error);
    if (error.response) {
      const message = error.response.data?.message || 'Login failed';
      const status = error.response.status;

      switch (status) {
        case 401:
          throw new Error('Invalid username or password');
        case 403:
          throw new Error('Access forbidden. Your account may be inactive.');
        case 404:
          throw new Error('Super admin login service not found. Please contact support.');
        case 500:
          throw new Error('Server error. Please try again later.');
        default:
          throw new Error(message || `Login failed (${status})`);
      }
    } else if (error.request) {
      throw new Error('Network error: Server is unavailable. Please try again or contact support.');
    } else {
      throw new Error('Error: ' + error.message);
    }
  }
};

/**
 * Logout super admin
 * @param {function} navigate - React Router's navigate function (optional)
 */
export const superAdminLogout = (navigate) => {
  // Clear super admin authentication data
  localStorage.removeItem('superAdminToken');
  localStorage.removeItem('superAdminRole');
  localStorage.removeItem('superAdminName');
  localStorage.removeItem('superAdminEmail');
  localStorage.removeItem('superAdminId');
  localStorage.removeItem('superAdminUsername');

  if (navigate) {
    navigate('/superadmin/login');
  } else {
    window.location.href = '/superadmin/login';
  }
};

/**
 * Store super admin authentication data in localStorage
 * @param {Object} data Authentication response data
 */
export const storeSuperAdminData = (data) => {
  localStorage.setItem('superAdminToken', data.accessToken);
  localStorage.setItem('superAdminRole', 'SUPER_ADMIN');
  localStorage.setItem('superAdminName', data.superAdmin?.fullName || data.superAdmin?.name || '');
  localStorage.setItem('superAdminEmail', data.superAdmin?.email || '');
  localStorage.setItem('superAdminId', data.superAdmin?.id?.toString() || '');
  localStorage.setItem('superAdminUsername', data.superAdmin?.username || '');
};

/**
 * Get the current super admin's authentication token
 * @returns {string|null} Auth token or null if not authenticated
 */
export const getSuperAdminToken = () => {
  return localStorage.getItem('superAdminToken');
};

/**
 * Check if super admin token is valid and not expired
 * @returns {boolean} Whether the token is valid
 */
export const isSuperAdminTokenValid = () => {
  const token = localStorage.getItem('superAdminToken');
  if (!token) return false;

  try {
    // Decode the JWT token
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );

    const { exp } = JSON.parse(jsonPayload);
    const currentTime = Math.floor(Date.now() / 1000);

    // Check if token is expired
    if (exp < currentTime) {
      // Clear invalid token
      superAdminLogout();
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error validating super admin token:', error);
    return false;
  }
};

/**
 * Check if super admin is authenticated with valid token
 * @returns {boolean} Whether super admin is authenticated
 */
export const isSuperAdminAuthenticated = () => {
  return isSuperAdminTokenValid();
};

/**
 * Get current super admin data from localStorage
 * @returns {Object} Super admin data
 */
export const getCurrentSuperAdmin = () => {
  return {
    id: localStorage.getItem('superAdminId'),
    name: localStorage.getItem('superAdminName'),
    email: localStorage.getItem('superAdminEmail'),
    username: localStorage.getItem('superAdminUsername'),
    role: localStorage.getItem('superAdminRole')
  };
};

/**
 * Get the authorization header needed for API requests
 * @returns {Object} Headers object with Authorization
 */
export const getSuperAdminAuthHeader = () => {
  const token = getSuperAdminToken();
  return token
    ? {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    : {};
};

export const changeSuperAdminPassword = async (currentPassword, newPassword) => {
  const response = await axios.put(
    `${API_URL}/superadmin/password`,
    { currentPassword, newPassword },
    { headers: getSuperAdminAuthHeader() }
  );
  return response.data;
};

// Export default service object
const superAdminAuthService = {
  superAdminLogin,
  superAdminLogout,
  getSuperAdminToken,
  isSuperAdminAuthenticated,
  getCurrentSuperAdmin,
  getSuperAdminAuthHeader
};

export default superAdminAuthService;

